import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import FormToggle from '@shared/atoms/FormFields/FormToggle';
import T from '@shared/atoms/Typography';
import Dialog from '@shared/molecules/Dialog';
import theme from '@shared/themes/default';
import { downloadFile, logger } from '@shared/utils';

import { useAppGlobals } from '../../../contexts/AppContext';

const USERS_API = '/users/download';

const ExportAllUsersDialog = ({
  onClose,
  open,
  setErrorMessage,
  setHasError,
  setHasSuccess,
  setSuccessMessage,
}) => {
  const { getRestClient } = useAppGlobals();
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState();
  const [singleUsers, setSingleUsers] = useState(false);
  const { t } = useTranslation();

  const restClient = getRestClient();

  useEffect(() => {
    setProcessing(false);
    setSingleUsers(false);
    setProgress();
  }, [open]);

  const actions = [
    {
      ariaLabel: t('generic.cancel'),
      label: t('generic.cancel'),
      handler: onClose,
      type: 'secondaryOutline',
    },
    {
      ariaLabel: t('backoffice.organizations.exportAllUsers.export'),
      label: t('backoffice.organizations.exportAllUsers.export'),
      handler: async () => {
        setProcessing(true);
        try {
          const { data } = await restClient.get(USERS_API, {
            params: {
              includeNonOrgMembers: singleUsers,
            },
          });
          downloadFile(data.signedUrl, data.fileName);
          setProcessing(false);
          setSuccessMessage(
            t('backoffice.organizations.exportAllUsers.successfullyExported')
          );
          setHasSuccess(true);
          onClose();
        } catch (error) {
          logger.error(error);
          setErrorMessage(
            t('backoffice.organizations.exportAllUsers.failedToExport')
          );
          setHasError(true);
          onClose();
        }
      },
      disabled: false,
      submit: true,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      actions={actions}
      title={t('backoffice.organizations.exportAllUsers.title')}
      ariaLabel={t('backoffice.organizations.exportAllUsers.title')}
      loading={processing}
    >
      {progress !== undefined ? (
        <T color={theme.palette.mainDark}>
          {t('backoffice.organizations.exportAllUsers.progress', {
            count: progress,
          })}
        </T>
      ) : (
        <FormToggle
          id="singleUsers"
          label={t(
            'backoffice.organizations.exportAllUsers.includeSingleSubscribers'
          )}
          name="singleUsers"
          handleChange={({ value }) => setSingleUsers(value)}
          theme="light"
          values={{ singleUsers }}
        />
      )}
    </Dialog>
  );
};

ExportAllUsersDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setHasError: PropTypes.func.isRequired,
  setHasSuccess: PropTypes.func.isRequired,
  setSuccessMessage: PropTypes.func.isRequired,
};

export default ExportAllUsersDialog;
